/**
 * InvoiceDetailModal - Modal for viewing invoice details with actions
 */

import { useState, useEffect, useMemo } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "../ui/dialog";
import {
  CheckCircle,
  PaperPlaneTilt,
  CurrencyDollar,
  Trash,
  PencilSimple,
  DownloadSimple,
  Printer,
  SpinnerGap,
} from "phosphor-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { formatCurrency } from "../../utils/formatters";
import { EntityDeliveryBanner } from "../DeliveryIssueWarning";
// Intentionally hidden in authenticated record modals only; public pages keep branding.
// import PoweredByFSPFooter from "../PoweredByFSPFooter";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Safe date formatter - prevents RangeError: Invalid time value
 * Returns "—" for null/undefined/invalid dates
 */
function safeDateFormat(value, formatStr = "MMM d, yyyy") {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return "—";
    return format(date, formatStr);
  } catch {
    return "—";
  }
}

/**
 * Safe date comparison - returns false for invalid dates
 */
function isDatePast(value) {
  if (!value) return false;
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return false;
    return date < new Date();
  } catch {
    return false;
  }
}

export default function InvoiceDetailModal({
  isOpen,
  onOpenChange,
  invoice,
  onSend,
  onRecordPayment,
  onEdit,
  onDelete,
  getStatusColor,
}) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [printing, setPrinting] = useState(false);
  
  // Full invoice detail fetched from API (includes deposit_credited, line items with negative deposit, etc.)
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // Extract invoice_id as a stable dependency
  const invoiceId = invoice?.invoice_id;
  
  // Generate correlation ID for diagnostic logging
  const fetchCid = useMemo(() => {
    return `cid_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
  }, [invoiceId, isOpen]);

  // DIAGNOSTIC: Log on every render
  console.info("[InvoiceDetailModal][RENDER]", { 
    fetchCid, 
    isOpen, 
    invoiceId, 
    hasInvoice: !!invoice,
    hasInvoiceDetail: !!invoiceDetail,
    loadingDetail 
  });

  // Fetch full invoice details when modal opens
  useEffect(() => {
    // Create abort controller for cleanup
    const abortController = new AbortController();
    
    console.info("[InvoiceDetailModal][FETCH_TRIGGER]", { 
      fetchCid, 
      isOpen, 
      invoiceId, 
      hasInvoice: !!invoice 
    });

    // Skip if modal not open or no invoice ID
    if (!isOpen || !invoiceId) {
      console.info("[InvoiceDetailModal][FETCH_SKIP]", { 
        fetchCid, 
        reason: !isOpen ? "modal_not_open" : "missing_invoice_id", 
        isOpen, 
        invoiceId 
      });
      // Reset state when modal closes
      if (!isOpen) {
        setInvoiceDetail(null);
        setDetailError(null);
      }
      return;
    }

    const fetchInvoiceDetail = async () => {
      // Use SAME-ORIGIN relative path (matches working Invoices.js pattern)
      const url = `${API}/invoices/${invoiceId}`;
      
      // Get token from localStorage (same key as LoginPage.js / Dashboard.js)
      const authToken = localStorage.getItem("auth_token") || localStorage.getItem("token");
      
      console.info("[InvoiceDetailModal][FETCH_START]", { 
        fetchCid, 
        url, 
        invoiceId,
        hasAuthToken: !!authToken,
        credentials: "include"
      });
      
      setLoadingDetail(true);
      setDetailError(null);
      
      try {
        // Match exact pattern from Invoices.js - credentials: include + optional Bearer token
        const response = await fetch(url, {
          method: "GET",
          credentials: "include",  // Include cookies (matches working calls)
          headers: {
            "Content-Type": "application/json",
            ...(authToken ? { "Authorization": `Bearer ${authToken}` } : {}),
          },
          signal: abortController.signal,
        });
        
        console.info("[InvoiceDetailModal][FETCH_DONE]", { 
          fetchCid, 
          status: response.status, 
          ok: response.ok,
          statusText: response.statusText
        });
        
        // Handle auth failures explicitly
        if (response.status === 401 || response.status === 403) {
          const errorMsg = `AUTH_REQUIRED (${response.status}) — invoice detail fetch blocked; falling back to list data`;
          console.warn("[InvoiceDetailModal][FETCH_AUTH_FAIL]", { 
            fetchCid, 
            status: response.status,
            hasAuthToken: !!authToken,
            url
          });
          setDetailError(errorMsg);
          setInvoiceDetail(invoice); // Fall back to list payload
          return;
        }
        
        if (response.ok) {
          const data = await response.json();
          console.info("[InvoiceDetailModal][FETCH_DATA_KEYS]", { 
            fetchCid, 
            keys: Object.keys(data || {}),
            balance_due: data?.balance_due,
            amount_paid: data?.amount_paid,
            deposit_credited: data?.deposit_credited,
            total: data?.total,
            itemsCount: data?.items?.length
          });
          setInvoiceDetail(data);
        } else {
          const errorText = await response.text().catch(() => "Unknown error");
          console.error("[InvoiceDetailModal][FETCH_ERROR_RESPONSE]", { 
            fetchCid, 
            status: response.status, 
            errorText 
          });
          setDetailError(`Failed to load: ${response.status} ${response.statusText}`);
          // Fall back to list payload if fetch fails
          setInvoiceDetail(invoice);
        }
      } catch (error) {
        // Ignore abort errors (expected on cleanup)
        if (error.name === 'AbortError') {
          console.info("[InvoiceDetailModal][FETCH_ABORTED]", { fetchCid });
          return;
        }
        console.error("[InvoiceDetailModal][FETCH_ERROR]", { 
          fetchCid, 
          error: String(error),
          errorName: error.name
        });
        setDetailError(`Fetch error: ${error.name || 'Unknown'}`);
        // Fall back to list payload if fetch fails
        setInvoiceDetail(invoice);
      } finally {
        if (!abortController.signal.aborted) {
          setLoadingDetail(false);
        }
      }
    };
    
    fetchInvoiceDetail();
    
    // Cleanup: abort fetch if component unmounts or dependencies change
    return () => {
      console.info("[InvoiceDetailModal][FETCH_CLEANUP]", { fetchCid });
      abortController.abort();
    };
  }, [isOpen, invoiceId]); // Stable dependencies - invoiceId extracted above

  // Use fetched detail if available, otherwise fall back to list payload
  const displayInvoice = invoiceDetail || invoice;

  // DEBUG: Show diagnostic info ONLY when explicitly enabled
  // To enable: set REACT_APP_SHOW_DEV_DEBUG=true in frontend/.env
  // Or set localStorage key: fsp_show_invoice_debug = "true"
  const showDebug = (
    process.env.REACT_APP_SHOW_DEV_DEBUG === "true" ||
    (typeof window !== 'undefined' && localStorage.getItem('fsp_show_invoice_debug') === 'true')
  );

  // Download PDF using blob-based approach (avoids popup blockers)
  const handleDownloadPdf = async () => {
    if (!displayInvoice) return;
    
    setDownloadingPdf(true);
    try {
      // Get auth token from localStorage
      const token = localStorage.getItem("token");
      
      // Fetch PDF as blob with authentication
      const pdfUrl = `${API}/invoices/${displayInvoice.invoice_id}/pdf`;
      const response = await fetch(pdfUrl, {
        method: "GET",
        credentials: "include",
        headers: token ? { "Authorization": `Bearer ${token}` } : {},
      });
      
      if (!response.ok) {
        throw new Error("Failed to generate PDF");
      }
      
      // Get the blob
      const blob = await response.blob();
      
      // Create object URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice_${displayInvoice.invoice_number || displayInvoice.invoice_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Invoice PDF downloaded successfully");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Print PDF - fetch blob and open print dialog
  const handlePrintPdf = async () => {
    if (!displayInvoice) return;
    
    setPrinting(true);
    try {
      // Get auth token from localStorage
      const token = localStorage.getItem("token");
      
      // Fetch PDF as blob with authentication
      const pdfUrl = `${API}/invoices/${displayInvoice.invoice_id}/pdf`;
      const response = await fetch(pdfUrl, {
        method: "GET",
        credentials: "include",
        headers: token ? { "Authorization": `Bearer ${token}` } : {},
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch PDF for printing");
      }
      
      // Get the blob
      const blob = await response.blob();
      
      // Create object URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a hidden iframe for printing
      const printFrame = document.createElement("iframe");
      printFrame.style.position = "fixed";
      printFrame.style.right = "0";
      printFrame.style.bottom = "0";
      printFrame.style.width = "0";
      printFrame.style.height = "0";
      printFrame.style.border = "none";
      printFrame.src = blobUrl;
      
      document.body.appendChild(printFrame);
      
      printFrame.onload = () => {
        setTimeout(() => {
          printFrame.contentWindow.print();
          // Clean up after print dialog closes
          setTimeout(() => {
            document.body.removeChild(printFrame);
            window.URL.revokeObjectURL(blobUrl);
          }, 1000);
        }, 500);
      };
      
      toast.success("Opening print dialog...");
      
    } catch (error) {
      console.error("[Print] Error:", error);
      toast.error("Failed to print. Please try again.");
    } finally {
      setPrinting(false);
    }
  };

  if (!displayInvoice) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="invoice-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase">
            {displayInvoice.invoice_number}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {/* Loading indicator */}
          {loadingDetail && (
            <div className="flex items-center justify-center py-4">
              <SpinnerGap size={24} className="animate-spin text-zinc-400" />
              <span className="ml-2 text-sm text-zinc-500">Loading details...</span>
            </div>
          )}
          
          {/* Delivery Issue Banner */}
          <EntityDeliveryBanner
            entityType="invoice"
            entityId={displayInvoice.invoice_id}
            customerId={displayInvoice.customer_id}
            onNavigateToCustomer={() => {
              onOpenChange(false);
              window.location.href = `/customers/${displayInvoice.customer_id}`;
            }}
            className="mb-4"
          />
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-lg">{displayInvoice.title}</h3>
                <p className="text-sm text-zinc-500">{displayInvoice.customer?.name}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {(displayInvoice.status === "draft" || displayInvoice.status === "sent") && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onEdit}
                      className="h-8 w-8 text-zinc-600 hover:text-[#FF6600] hover:bg-[#FF6600]/10"
                      data-testid="edit-invoice-btn"
                    >
                      <PencilSimple size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onDelete}
                      className="h-8 w-8 text-zinc-600 hover:text-red-600 hover:bg-red-50"
                      data-testid="delete-invoice-btn"
                    >
                      <Trash size={18} />
                    </Button>
                  </>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  className="h-8 w-8 text-zinc-600 hover:text-blue-600 hover:bg-blue-50"
                  data-testid="download-invoice-pdf-btn"
                  title="Download PDF"
                >
                  {downloadingPdf ? (
                    <SpinnerGap size={18} className="animate-spin" />
                  ) : (
                    <DownloadSimple size={18} />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handlePrintPdf}
                  disabled={printing}
                  className="h-8 w-8 text-zinc-600 hover:text-green-600 hover:bg-green-50"
                  data-testid="print-invoice-pdf-btn"
                  title="Print"
                >
                  {printing ? (
                    <SpinnerGap size={18} className="animate-spin" />
                  ) : (
                    <Printer size={18} />
                  )}
                </Button>
                <Badge variant="outline" className={getStatusColor(displayInvoice.status)}>
                  {displayInvoice.status}
                </Badge>
              </div>
            </div>

            {/* Dates */}
            <div className="flex gap-6 text-sm flex-wrap">
              <div>
                <span className="text-zinc-400">Created: </span>
                <span>{safeDateFormat(displayInvoice.created_at)}</span>
              </div>
              {displayInvoice.sent_at && (
                <div>
                  <span className="text-zinc-400">Sent: </span>
                  <span>{safeDateFormat(displayInvoice.sent_at)}</span>
                </div>
              )}
              {displayInvoice.due_date && (
                <div>
                  <span className="text-zinc-400">Due: </span>
                  <span className={isDatePast(displayInvoice.due_date) && displayInvoice.status !== "paid" ? "text-red-600 font-medium" : ""}>
                    {safeDateFormat(displayInvoice.due_date)}
                  </span>
                </div>
              )}
            </div>

            {/* Line Items */}
            <div className="border border-zinc-200 rounded-md overflow-hidden">
              <div className="bg-zinc-50 p-3 text-xs font-medium text-zinc-500 uppercase grid grid-cols-12 gap-4">
                <div className="col-span-5">Description</div>
                <div className="col-span-2 text-center">Qty</div>
                <div className="col-span-2 text-right">Price</div>
                <div className="col-span-1 text-center">Tax</div>
                <div className="col-span-2 text-right">Total</div>
              </div>
              {displayInvoice.items?.map((item, idx) => {
                // Handle both price and unit_price for line items (backend uses unit_price for deposit lines)
                const itemPrice = item.price ?? item.unit_price ?? 0;
                const isNegative = itemPrice < 0;
                return (
                <div key={idx} className={`p-3 border-t border-zinc-100 grid grid-cols-12 gap-4 text-sm ${isNegative ? 'bg-emerald-50' : ''}`}>
                  <div className={`col-span-5 ${isNegative ? 'text-emerald-700' : ''}`}>{item.description || "Item"}</div>
                  <div className="col-span-2 text-center">{item.quantity}</div>
                  <div className={`col-span-2 text-right ${isNegative ? 'text-emerald-700' : ''}`}>{formatCurrency(itemPrice)}</div>
                  <div className="col-span-1 text-center">
                    {item.is_taxable && <CheckCircle size={16} className="text-emerald-500 mx-auto" />}
                  </div>
                  <div className={`col-span-2 text-right font-medium ${isNegative ? 'text-emerald-700' : ''}`}>
                    {formatCurrency(item.quantity * itemPrice)}
                  </div>
                </div>
              );
              })}
            </div>

            {/* Totals - using fetched displayInvoice data directly (no local recomputation) */}
            <div className="bg-zinc-50 p-4 rounded-md space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>{formatCurrency(displayInvoice.subtotal)}</span>
              </div>
              {(displayInvoice.tax > 0 || (displayInvoice.tax_rate_percent > 0 && displayInvoice.taxable_amount > 0)) && (
                <div className="flex justify-between text-sm">
                  <span>
                    Tax ({displayInvoice.tax_rate_percent || 0}%
                    {displayInvoice.taxable_amount > 0 && ` on ${formatCurrency(displayInvoice.taxable_amount)}`})
                  </span>
                  <span>{formatCurrency(displayInvoice.tax)}</span>
                </div>
              )}
              <div className="flex justify-between font-medium border-t border-zinc-200 pt-2">
                <span>Total</span>
                <span>{formatCurrency(displayInvoice.total)}</span>
              </div>
              {/* Show deposit credited explicitly if present */}
              {displayInvoice.deposit_credited > 0 && (
                <div className="flex justify-between text-sm text-emerald-600">
                  <span>Deposit received</span>
                  <span>-{formatCurrency(displayInvoice.deposit_credited)}</span>
                </div>
              )}
              {/* Show other payments (amount_paid minus deposit_credited) */}
              {displayInvoice.amount_paid > 0 && (displayInvoice.amount_paid !== displayInvoice.deposit_credited) && (
                <div className="flex justify-between text-sm text-emerald-600">
                  <span>{displayInvoice.deposit_credited > 0 ? 'Other Payments' : 'Paid'}</span>
                  <span>-{formatCurrency(displayInvoice.amount_paid - (displayInvoice.deposit_credited || 0))}</span>
                </div>
              )}
              <div className="flex justify-between font-heading text-xl font-bold border-t border-zinc-200 pt-2">
                <span>Balance Due</span>
                <span className={displayInvoice.balance_due > 0 ? "text-amber-600" : "text-emerald-600"}>
                  {formatCurrency(displayInvoice.balance_due)}
                </span>
              </div>
            </div>

            {displayInvoice.notes && (
              <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-md">
                <p className="text-xs text-zinc-400 uppercase mb-1">Notes</p>
                <p className="text-sm">{displayInvoice.notes}</p>
              </div>
            )}
            
            {/* Intentionally hidden in authenticated record modals only; public pages keep branding. */}
          </div>
        </DialogBody>

        {/* DEV MODE: Debug panel for preview environments */}
        {showDebug && (
          <div className="mx-4 mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs font-mono">
            <div className="font-bold text-yellow-800 mb-1">🔧 DEV DEBUG (InvoiceDetailModal)</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-yellow-700">
              <span>invoiceId:</span><span>{invoiceId || 'null'}</span>
              <span>isOpen:</span><span>{String(isOpen)}</span>
              <span>loadingDetail:</span><span>{String(loadingDetail)}</span>
              <span>hasInvoiceDetail:</span><span>{String(!!invoiceDetail)}</span>
              <span>detailError:</span><span className="text-red-600">{detailError || 'none'}</span>
              <span>balance_due (display):</span><span>{displayInvoice?.balance_due}</span>
              <span>amount_paid (display):</span><span>{displayInvoice?.amount_paid}</span>
              <span>deposit_credited:</span><span>{displayInvoice?.deposit_credited || 'null'}</span>
              <span>source:</span><span className={invoiceDetail ? 'text-green-600 font-bold' : 'text-orange-600 font-bold'}>{invoiceDetail ? 'API_FETCH ✓' : 'LIST_PROP ⚠'}</span>
              <span>hasAuthToken:</span><span>{String(!!(localStorage.getItem("auth_token") || localStorage.getItem("token")))}</span>
              <span>API base:</span><span className="truncate" title={API}>{API?.substring(0, 40)}...</span>
            </div>
          </div>
        )}

        {/* Actions Footer - use original invoice prop for action callbacks to maintain list sync */}
        <DialogFooter className="flex-wrap">
          {/* Record Payment - available for draft and sent (not paid/void) so users can log
              cash / in-person payments without sending the invoice first. Reuses the exact
              same onRecordPayment handler used for sent invoices — no logic change. */}
          {displayInvoice.status !== "paid" && displayInvoice.status !== "void" && (displayInvoice.balance_due > 0 || displayInvoice.balance_due === undefined) && (
            <Button
              onClick={() => onRecordPayment(displayInvoice)}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="record-payment-btn"
            >
              <CurrencyDollar size={18} className="mr-2" />
              Record Payment
            </Button>
          )}
          {/* Send/Resend button - available for draft, sent, and revision_pending invoices (not paid/void) */}
          {(displayInvoice.status === "draft" || displayInvoice.status === "sent" || displayInvoice.sent_at || displayInvoice.revision_pending) && 
           displayInvoice.status !== "paid" && displayInvoice.status !== "void" && (
            <Button
              onClick={() => onSend(displayInvoice.invoice_id)}
              className="bg-blue-500 hover:bg-blue-600 text-white"
              data-testid="send-invoice-btn"
            >
              <PaperPlaneTilt size={18} className="mr-2" />
              {displayInvoice.status === "draft" ? "Send Invoice" : "Resend Invoice"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
